import type { DatasetMetadata } from '../types/QueryTypes';
export const REQUEST_METADATA: DatasetMetadata = {
  fields: {
    datasetType: {
      type: 'string',
      datasets: ['request'],
      description: 'Dataset discriminator',
      synonyms: ['type', 'kind']
    },
    acc_name: {
      type: 'string',
      datasets: ['request'],
      description: 'Customer account name',
      synonyms: ['account', 'company', 'customer', 'client']
    },
    opp_id: {
      type: 'string',
      datasets: ['request'],
      description: 'Opportunity ID',
      synonyms: ['id', 'deal id', 'dr id', 'opportunity id', 'deal', 'deals']
    },
    opp_gnarr_grand_total: {
      type: 'number',
      datasets: ['request'],
      description: 'Grand total annual recurring revenue',
      synonyms: ['gnarr', 'revenue', 'amount', 'value', 'grand total arr']
    },
    opp_gnarr: {
      type: 'number',
      datasets: ['request'],
      description: 'Opportunity revenue',
      synonyms: ['opp gnarr', 'opportunity gnarr', 'opportunity revenue']
    },
    opp_status: {
      type: 'string',
      datasets: ['request'],
      description: 'Opportunity status',
      synonyms: ['opp status', 'opportunity status']
    },
    opp_adjusted_commit: {
      type: 'string',
      datasets: ['request'],
      description: 'Commit status',
      synonyms: ['adjusted commit', 'commit', 'adjusted']
    },
    opp_close_q: {
      type: 'string',
      datasets: ['request'],
      description: 'Expected close quarter',
      synonyms: ['close quarter', 'close q', 'quarter']
    },
    opp_region: {
      type: 'string',
      datasets: ['request'],
      description: 'Geographic region',
      synonyms: ['region', 'geo', 'geography', 'sales region'],
      enumValues: ['AMER', 'EMEA', 'JAPAC'],
      valueAliases: {
        america: 'AMER',
        americas: 'AMER',
        europe: 'EMEA',
        apac: 'JAPAC',
        'asia pacific': 'JAPAC'
      }
    },
    opp_segment: {
      type: 'string',
      datasets: ['request'],
      description: 'Business segment',
      synonyms: ['segment', 'business unit', 'bu'],
      valueAliases: {
        ind: 'Industry'
      }
    },
    opp_segment_sales: {
      type: 'string',
      datasets: ['request'],
      description: 'Sales segment',
      synonyms: ['segment sales', 'sales segment'],
      valueAliases: {
        trans: 'Transformational',
        transformation: 'Transformational'
      }
    },
    opp_market_area: {
      type: 'string',
      datasets: ['request'],
      description: 'Market area',
      synonyms: ['market area', 'market', 'market segment'],
      valueAliases: {
        us: 'United States',
        usa: 'United States'
      }
    },
    req_id: {
      type: 'string',
      datasets: ['request'],
      description: 'Request registration ID',
      synonyms: ['request id', 'issue id', 'dr id']
    },
    req_name: {
      type: 'string',
      datasets: ['request'],
      description: 'Request name',
      synonyms: ['request name', 'issue name']
    },
    req_age: {
      type: 'number',
      datasets: ['request'],
      description: 'Days since entry',
      synonyms: ['age', 'days', 'duration']
    },
    req_status: {
      type: 'enum',
      datasets: ['request'],
      description: 'Request status',
      synonyms: ['status', 'state', 'stage'],
      enumValues: ['In Progress', 'Closed', 'On Hold', 'Cancelled']
    },
    req_entry: {
      type: 'string',
      datasets: ['request'],
      description: 'Entry date',
      synonyms: ['entry', 'created', 'created date']
    },
    req_completion: {
      type: 'string',
      datasets: ['request'],
      description: 'Completion date',
      synonyms: ['completion', 'completed', 'closed date']
    },
    req_created_fy: {
      type: 'string',
      datasets: ['request'],
      description: 'Created fiscal year',
      synonyms: ['created fy', 'created fiscal year']
    },
    req_created_q: {
      type: 'string',
      datasets: ['request'],
      description: 'Created quarter',
      synonyms: ['created quarter', 'created q']
    },
    req_closed_fy: {
      type: 'string',
      datasets: ['request'],
      description: 'Closed fiscal year (FY of the request completion date; empty when not closed)',
      synonyms: ['closed fy', 'closed fiscal year', 'completed fy', 'completion fy']
    },
    req_closed_q: {
      type: 'string',
      datasets: ['request'],
      description: 'Closed quarter (quarter of the request completion date; empty when not closed). Not the opportunity close quarter.',
      synonyms: ['closed quarter', 'completed quarter', 'completion quarter', 'closed q']
    },
    req_time: {
      type: 'string',
      datasets: ['request'],
      description: 'Time request was assigned to SC',
      synonyms: ['request time', 'assigned time']
    },
    req_offering: {
      type: 'string',
      datasets: ['request'],
      description: 'Offering type',
      synonyms: ['offering', 'solution', 'package']
    },
    req_products: {
      type: 'string',
      datasets: ['request'],
      description: 'Products involved',
      synonyms: ['products', 'product', 'solutions', 'tools']
    },
    req_market: {
      type: 'string',
      datasets: ['request'],
      description: 'Request market',
      synonyms: ['market']
    },
    req_sub_region: {
      type: 'string',
      datasets: ['request'],
      description: 'Sub-region or territory',
      synonyms: ['sub region', 'sub-region', 'subregion', 'territory'],
      enumValues: ['CANADA', 'LATAM', 'IND (Key)', 'IND (Trans)', 'ENT', 'SCALE', 'OTHERS', 'UKI', 'MEA', 'CENTRAL', 'SOUTH WESTERN', 'ANZ', 'JAPAN', 'SEA'],
      valueAliases: {
        'industry transformation': 'IND (Trans)',
        'industry trans': 'IND (Trans)',
        'industry key': 'IND (Key)'
      }
    },
    req_sv_region: {
      type: 'string',
      datasets: ['request'],
      description: 'SV region',
      synonyms: ['sv region', 'sv geo']
    },
    user_sc_name: {
      type: 'string',
      datasets: ['request'],
      description: 'Solution consultant assigned',
      synonyms: ['sc', 'consultant', 'solution consultant', 'owner']
    }
  }
};
export const TOUCHPOINT_METADATA: DatasetMetadata = {
  fields: {
    datasetType: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Dataset discriminator',
      synonyms: ['type', 'kind']
    },
    acc_name: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Customer account name',
      synonyms: ['account', 'company', 'customer', 'client']
    },
    opp_id: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Opportunity ID',
      synonyms: ['id', 'deal id', 'opportunity id']
    },
    opp_age: {
      type: 'number',
      datasets: ['touchpoint'],
      description: 'Opportunity age',
      synonyms: ['age', 'days', 'duration']
    },
    opp_gnarr_grand_total: {
      type: 'number',
      datasets: ['touchpoint'],
      description: 'Grand total annual recurring revenue',
      synonyms: ['gnarr', 'revenue', 'amount', 'value', 'grand total arr']
    },
    opp_gnarr: {
      type: 'number',
      datasets: ['touchpoint'],
      description: 'Opportunity revenue',
      synonyms: ['opp gnarr', 'opportunity gnarr', 'opportunity revenue']
    },
    opp_close_q: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Close quarter',
      synonyms: ['close quarter', 'close q', 'quarter']
    },
    opp_region: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Geographic region',
      synonyms: ['region', 'geo', 'geography'],
      enumValues: ['AMER', 'EMEA', 'JAPAC'],
      valueAliases: {
        america: 'AMER',
        americas: 'AMER',
        europe: 'EMEA',
        apac: 'JAPAC',
        'asia pacific': 'JAPAC'
      }
    },
    opp_segment: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Business segment',
      synonyms: ['segment', 'business unit', 'bu'],
      valueAliases: {
        ind: 'Industry'
      }
    },
    opp_segment_sales: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Sales segment',
      synonyms: ['segment sales', 'sales segment'],
      valueAliases: {
        trans: 'Transformational',
        transformation: 'Transformational'
      }
    },
    opp_market_area: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Market area',
      synonyms: ['market area', 'market', 'market segment'],
      valueAliases: {
        us: 'United States',
        usa: 'United States'
      }
    },
    tp_id: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Touchpoint identifier',
      synonyms: ['touchpoint id', 'task id']
    },
    tp_name: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Touchpoint name',
      synonyms: ['name', 'touchpoint name', 'task name', 'title']
    },
    tp_type: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Type of touchpoint activity',
      synonyms: ['touchpoint type', 'type', 'activity type', 'meeting type']
    },
    tp_status: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Touchpoint status',
      synonyms: ['status', 'state', 'stage']
    },
    tp_entry: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Entry date',
      synonyms: ['entry', 'created', 'created date']
    },
    tp_date: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Touchpoint date',
      synonyms: ['touchpoint date', 'tps touchpoint date']
    },
    tp_completion: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Completion date',
      synonyms: ['completion', 'completed', 'closed date']
    },
    tp_actual_start_date: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Actual start date',
      synonyms: ['start date', 'actual start', 'begin date']
    },
    tp_created_fy: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Created fiscal year',
      synonyms: ['created fy', 'created fiscal year']
    },
    tp_created_q: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Created quarter',
      synonyms: ['created quarter', 'created q']
    },
    tp_solution_comments: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Raw solution comments',
      synonyms: ['solution comments', 'sc comments']
    },
    tp_comment_text: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Plain text comments',
      synonyms: ['comment text', 'comments', 'notes', 'description']
    },
    user_sc_name: {
      type: 'string',
      datasets: ['touchpoint'],
      description: 'Solution consultant assigned',
      synonyms: ['sc', 'consultant', 'solution consultant', 'owner']
    }
  }
};
type MergedFieldMetadata = {
  type: string;
  datasets: string[];
  synonyms: string[];
  enumValues?: string[];
  valueAliases?: Record<string, string>;
};
export const ALL_FIELDS: Record<string, MergedFieldMetadata> = (() => {
  const merged: Record<string, MergedFieldMetadata> = {};
  for (const [name, meta] of Object.entries(TOUCHPOINT_METADATA.fields)) {
    if (name === 'datasetType') continue;
    merged[name] = {
      ...meta,
      datasets: [...meta.datasets]
    };
  }
  for (const [name, meta] of Object.entries(REQUEST_METADATA.fields)) {
    if (name === 'datasetType') continue;
    if (merged[name]) {
      for (const ds of meta.datasets) {
        if (!merged[name].datasets.includes(ds)) merged[name].datasets.push(ds);
      }
    } else {
      merged[name] = {
        ...meta,
        datasets: [...meta.datasets]
      };
    }
  }
  return merged;
})();